import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { fr, enUS, it } from 'date-fns/locale';

// Configure date-fns locales
export const dateLocales: { [key: string]: Locale } = {
  fr,
  en: enUS,
  it
};

const resources = {
  fr: {
    translation: {
      common: {
        welcome: 'Bienvenue',
        login: 'Connexion',
        logout: 'Déconnexion',
        email: 'Email',
        password: 'Mot de passe',
        submit: 'Valider',
        cancel: 'Annuler',
        save: 'Enregistrer',
        delete: 'Supprimer',
        edit: 'Modifier',
        create: 'Créer',
        search: 'Rechercher',
        loading: 'Chargement...',
        noData: 'Aucune donnée disponible',
        error: 'Une erreur est survenue',
        success: 'Opération réussie',
        confirm: 'Confirmer',
        actions: 'Actions',
        darkMode: "Mode sombre",
        lightMode: "Mode clair",
        refresh: "Actualiser",
        export: "Exporter",
        download: "Télécharger",
        close: "Fermer",
        details: "Détails",
        back: "Retour",
        name: "Nom",
        address: "Adresse",
        phone: "Téléphone",
        language: "Langue",
        role: "Rôle",
        status: "Statut",
        date: "Date",
        period: "Période",
        services: "Services",
        guests: "Invités",
        property: "Logement",
        properties: "Logements",
        booking: "Réservation",
        bookings: "Réservations",
        user: "Utilisateur",
        users: "Utilisateurs",
        all: "Tous",
        current: "En cours",
        upcoming: "À venir",
        past: "Passé",
        yes: "Oui",
        no: "Non",
        required: "Champ requis",
        optional: "Optionnel",
        people: "personnes",
        today: "Aujourd'hui",
        month: "Mois",
        week: "Semaine",
        more: "Plus",
        less: 'Moins',
        contact: "Contact",
        settings: "Paramètres",
        changePassword: "Changer le mot de passe",
        currentPassword: "Mot de passe actuel",
        newPassword: "Nouveau mot de passe",
        confirmPassword: "Confirmer le mot de passe",
        saving: "Enregistrement...",
        languages: {
          fr: "Français",
          en: "English",
          it: "Italiano"
        }
      },
      auth: {
        signIn: "Se connecter",
        signUp: "S'inscrire",
        forgotPassword: "Mot de passe oublié ?",
        resetPassword: "Réinitialiser le mot de passe",
        createAccount: "Créer un compte",
        accountPending: "Compte en attente d'activation",
        pendingMessage: "Votre compte doit être validé par un administrateur avant de pouvoir accéder à l'application.",
        accessDenied: "Accès refusé",
        accessDeniedMessage: "Vous n'avez pas les permissions nécessaires pour accéder à cette page.",
        errors: {
          invalidCredentials: "Email ou mot de passe incorrect",
          emailRequired: "L'email est requis",
          passwordRequired: "Le mot de passe est requis",
          passwordLength: "Le mot de passe doit contenir au moins 6 caractères",
          emailInvalid: "Email invalide"
        }
      },
      dashboard: {
        title: "Tableau de bord",
        overview: "Vue d'ensemble",
        stats: {
          title: "Statistiques",
          properties: "Logements",
          bookings: "Réservations",
          users: "Utilisateurs",
          occupancyRate: "Taux d'occupation",
          averageNights: "Nuits moyennes",
          todayArrivals: "Arrivées du jour",
          todayDepartures: "Départs du jour",
          currentStays: "Séjours en cours"
        },
        activity: {
          title: "Activités récentes",
          noActivity: "Aucune activité récente",
          types: {
            create: "Création",
            update: "Modification",
            delete: "Suppression"
          }
        }
      },
      statistics: {
        title: "Statistiques",
        period: {
          daily: "Journalier",
          weekly: "Hebdomadaire",
          monthly: "Mensuel",
          yearly: "Annuel"
        },
        metrics: {
          bookings: "Réservations",
          occupancyRate: "Taux d'occupation",
          averageNights: "Nuits moyennes",
          revenue: "Revenus",
          nights: "Nuits"
        },
        charts: {
          monthlyEvolution: "Évolution mensuelle",
          occupancyByProperty: "Occupation par logement",
          bookingDistribution: "Distribution des réservations",
          propertyDetails: "Détails des logements"
        }
      },
      bookings: {
        title: "Réservations",
        new: "Nouvelle réservation",
        edit: "Modifier la réservation",
        delete: "Supprimer la réservation",
        deleteConfirm: "Êtes-vous sûr de vouloir supprimer cette réservation ? Cette action est irréversible.",
        filters: {
          services: "Services",
          period: "Période",
          allServices: "Tous les services",
          cleaning: "Ménage",
          bedSheets: "Draps",
          both: "Ménage + Draps",
          currentAndUpcoming: "Séjours en cours et à venir",
          allBookings: "Toutes les réservations"
        },
        status: {
          current: "En cours",
          upcoming: "À venir",
          past: "Passé"
        },
        form: {
          property: "Logement",
          selectProperty: "Sélectionner un logement",
          guestCount: "Nombre d'invités",
          guestFirstName: "Prénom de l'invité",
          guestLastName: "Nom de l'invité",
          checkIn: "Date d'arrivée",
          checkOut: "Date de départ",
          guestEmail: "Email de l'invité",
          guestPhone: "Téléphone de l'invité",
          doorCode: "Code d'accès",
          comments: "Commentaires",
          cleaningService: "Service de ménage",
          bedSheetsService: "Service de draps",
          validation: {
            propertyRequired: "Le logement est requis",
            guestCountRequired: "Le nombre d'invités est requis",
            guestCountMin: "Minimum 1 invité",
            guestCountMax: "Le nombre d'invités ne peut pas dépasser la capacité du logement",
            guestFirstNameRequired: "Le prénom de l'invité est requis",
            guestLastNameRequired: "Le nom de l'invité est requis",
            checkInRequired: "La date d'arrivée est requise",
            checkOutRequired: "La date de départ est requise",
            checkInPast: "La date d'arrivée ne peut pas être dans le passé",
            checkOutBeforeCheckIn: "La date de départ doit être après la date d'arrivée",
            emailInvalid: "Email invalide"
          }
        },
        notifications: {
          created: "Réservation créée",
          updated: "Réservation modifiée",
          deleted: "Réservation supprimée",
          error: "Erreur lors de l'enregistrement"
        }
      },
      properties: {
        title: "Logements",
        new: "Nouveau logement",
        edit: "Modifier le logement",
        delete: "Supprimer le logement",
        deleteConfirm: "Êtes-vous sûr de vouloir supprimer ce logement ? Cette action est irréversible.",
        search: "Rechercher un logement...",
        tooltip: "Gérez vos logements et leurs informations",
        form: {
          name: "Nom du logement",
          address: "Adresse",
          capacity: "Capacité (nombre de personnes)",
          owner: "Propriétaire",
          selectOwner: "Sélectionner un propriétaire",
          validation: {
            nameRequired: "Le nom est requis",
            addressRequired: "L'adresse est requise",
            capacityRequired: "La capacité est requise",
            capacityMin: "Capacité minimum de 1 personne",
            capacityMax: "Capacité maximum de 50 personnes",
            ownerRequired: "Le propriétaire est requis"
          }
        },
        stats: {
          title: "Statistiques du logement",
          totalBookings: "Total des réservations",
          averageNights: "Nuits moyennes",
          occupancyRate: "Taux d'occupation",
          monthlyBookings: "Réservations mensuelles"
        },
        notifications: {
          created: "Logement créé",
          updated: "Logement modifié",
          deleted: "Logement supprimé",
          error: "Erreur lors de l'enregistrement"
        }
      },
      users: {
        title: "Utilisateurs",
        new: "Nouvel utilisateur",
        edit: "Modifier l'utilisateur",
        delete: "Supprimer l'utilisateur",
        deleteConfirm: "Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.",
        search: "Rechercher un utilisateur...",
        roles: {
          guest: "Invité",
          support: "Support",
          owner: "Propriétaire",
          admin: "Administrateur",
          super_admin: "Super Administrateur"
        },
        form: {
          email: "Email",
          password: "Mot de passe",
          firstName: "Prénom",
          lastName: "Nom",
          phone: "Téléphone",
          role: "Rôle",
          language: "Langue préférée",
          validation: {
            emailRequired: "L'email est requis",
            emailInvalid: "Email invalide",
            passwordRequired: "Le mot de passe est requis",
            passwordLength: "Le mot de passe doit contenir au moins 6 caractères",
            firstNameRequired: "Le prénom est requis",
            lastNameRequired: "Le nom est requis"
          }
        },
        notifications: {
          created: "Utilisateur créé",
          updated: "Utilisateur modifié",
          deleted: "Utilisateur supprimé",
          error: "Erreur lors de l'enregistrement"
        }
      }
    }
  },
  en: {
    translation: {
      common: {
        welcome: 'Welcome',
        login: 'Login',
        logout: 'Logout',
        email: 'Email',
        password: 'Password',
        submit: 'Submit',
        cancel: 'Cancel',
        save: 'Save',
        delete: 'Delete',
        edit: 'Edit',
        create: 'Create',
        search: 'Search',
        loading: 'Loading...',
        noData: 'No data available',
        error: 'An error occurred',
        success: 'Operation successful',
        confirm: 'Confirm',
        actions: 'Actions',
        darkMode: "Dark mode",
        lightMode: "Light mode",
        refresh: "Refresh",
        export: "Export",
        download: "Download",
        close: "Close",
        details: "Details",
        back: "Back",
        name: "Name",
        address: "Address",
        phone: "Phone",
        language: "Language",
        role: "Role",
        status: "Status",
        date: "Date",
        period: "Period",
        services: "Services",
        guests: "Guests",
        property: "Property",
        properties: "Properties",
        booking: "Booking",
        bookings: "Bookings",
        user: "User",
        users: "Users",
        all: "All",
        current: "Current",
        upcoming: "Upcoming",
        past: "Past",
        yes: "Yes",
        no: "No",
        required: "Required field",
        optional: "Optional",
        people: "people",
        today: "Today",
        month: "Month",
        week: "Week",
        more: "More",
        less: 'Less',
        contact: "Contact",
        settings: "Settings",
        changePassword: "Change password",
        currentPassword: "Current password",
        newPassword: "New password",
        confirmPassword: "Confirm password",
        saving: "Saving...",
        languages: {
          fr: "Français",
          en: "English",
          it: "Italiano"
        }
      },
      auth: {
        signIn: "Sign in",
        signUp: "Sign up",
        forgotPassword: "Forgot password?",
        resetPassword: "Reset password",
        createAccount: "Create account",
        accountPending: "Account pending activation",
        pendingMessage: "Your account needs to be validated by an administrator before you can access the application.",
        accessDenied: "Access denied",
        accessDeniedMessage: "You don't have the necessary permissions to access this page.",
        errors: {
          invalidCredentials: "Invalid email or password",
          emailRequired: "Email is required",
          passwordRequired: "Password is required",
          passwordLength: "Password must be at least 6 characters long",
          emailInvalid: "Invalid email"
        }
      },
      dashboard: {
        title: "Dashboard",
        overview: "Overview",
        stats: {
          title: "Statistics",
          properties: "Properties",
          bookings: "Bookings",
          users: "Users",
          occupancyRate: "Occupancy rate",
          averageNights: "Average nights",
          todayArrivals: "Today's arrivals",
          todayDepartures: "Today's departures",
          currentStays: "Current stays"
        },
        activity: {
          title: "Recent activity",
          noActivity: "No recent activity",
          types: {
            create: "Creation",
            update: "Update",
            delete: "Deletion"
          }
        }
      },
      statistics: {
        title: "Statistics",
        period: {
          daily: "Daily",
          weekly: "Weekly",
          monthly: "Monthly",
          yearly: "Yearly"
        },
        metrics: {
          bookings: "Bookings",
          occupancyRate: "Occupancy rate",
          averageNights: "Average nights",
          revenue: "Revenue",
          nights: "Nights"
        },
        charts: {
          monthlyEvolution: "Monthly evolution",
          occupancyByProperty: "Occupancy by property",
          bookingDistribution: "Booking distribution",
          propertyDetails: "Property details"
        }
      },
      bookings: {
        title: "Bookings",
        new: "New booking",
        edit: "Edit booking",
        delete: "Delete booking",
        deleteConfirm: "Are you sure you want to delete this booking? This action cannot be undone.",
        filters: {
          services: "Services",
          period: "Period",
          allServices: "All services",
          cleaning: "Cleaning",
          bedSheets: "Bed sheets",
          both: "Cleaning + Sheets",
          currentAndUpcoming: "Current and upcoming stays",
          allBookings: "All bookings"
        },
        status: {
          current: "Current",
          upcoming: "Upcoming",
          past: "Past"
        },
        form: {
          property: "Property",
          selectProperty: "Select a property",
          guestCount: "Number of guests",
          guestFirstName: "Guest first name",
          guestLastName: "Guest last name",
          checkIn: "Check-in date",
          checkOut: "Check-out date",
          guestEmail: "Guest email",
          guestPhone: "Guest phone",
          doorCode: "Access code",
          comments: "Comments",
          cleaningService: "Cleaning service",
          bedSheetsService: "Bed sheets service",
          validation: {
            propertyRequired: "Property is required",
            guestCountRequired: "Number of guests is required",
            guestCountMin: "Minimum 1 guest",
            guestCountMax: "Number of guests cannot exceed property capacity",
            guestFirstNameRequired: "Guest first name is required",
            guestLastNameRequired: "Guest last name is required",
            checkInRequired: "Check-in date is required",
            checkOutRequired: "Check-out date is required",
            checkInPast: "Check-in date cannot be in the past",
            checkOutBeforeCheckIn: "Check-out date must be after check-in date",
            emailInvalid: "Invalid email"
          }
        },
        notifications: {
          created: "Booking created",
          updated: "Booking updated",
          deleted: "Booking deleted",
          error: "Error saving booking"
        }
      },
      properties: {
        title: "Properties",
        new: "New property",
        edit: "Edit property",
        delete: "Delete property",
        deleteConfirm: "Are you sure you want to delete this property? This action cannot be undone.",
        search: "Search properties...",
        tooltip: "Manage your properties and their information",
        form: {
          name: "Property name",
          address: "Address",
          capacity: "Capacity (number of people)",
          owner: "Owner",
          selectOwner: "Select an owner",
          validation: {
            nameRequired: "Name is required",
            addressRequired: "Address is required",
            capacityRequired: "Capacity is required",
            capacityMin: "Minimum capacity is 1 person",
            capacityMax: "Maximum capacity is 50 people",
            ownerRequired: "Owner is required"
          }
        },
        stats: {
          title: "Property statistics",
          totalBookings: "Total bookings",
          averageNights: "Average nights",
          occupancyRate: "Occupancy rate",
          monthlyBookings: "Monthly bookings"
        },
        notifications: {
          created: "Property created",
          updated: "Property updated",
          deleted: "Property deleted",
          error: "Error saving property"
        }
      },
      users: {
        title: "Users",
        new: "New user",
        edit: "Edit user",
        delete: "Delete user",
        deleteConfirm: "Are you sure you want to delete this user? This action cannot be undone.",
        search: "Search users...",
        roles: {
          guest: "Guest",
          support: "Support",
          owner: "Owner",
          admin: "Administrator",
          super_admin: "Super Administrator"
        },
        form: {
          email: "Email",
          password: "Password",
          firstName: "First name",
          lastName: "Last name",
          phone: "Phone",
          role: "Role",
          language: "Preferred language",
          validation: {
            emailRequired: "Email is required",
            emailInvalid: "Invalid email",
            passwordRequired: "Password is required",
            passwordLength: "Password must be at least 6 characters long",
            firstNameRequired: "First name is required",
            lastNameRequired: "Last name is required"
          }
        },
        notifications: {
          created: "User created",
          updated: "User updated",
          deleted: "User deleted",
          error: "Error saving user"
        }
      }
    }
  },
  it: {
    translation: {
      common: {
        welcome: 'Benvenuto',
        login: 'Accesso',
        logout: 'Disconnetti',
        email: 'Email',
        password: 'Password',
        submit: 'Invia',
        cancel: 'Annulla',
        save: 'Salva',
        delete: 'Elimina',
        edit: 'Modifica',
        create: 'Crea',
        search: 'Cerca',
        loading: 'Caricamento...',
        noData: 'Nessun dato disponibile',
        error: 'Si è verificato un errore',
        success: 'Operazione completata',
        confirm: 'Conferma',
        actions: 'Azioni',
        darkMode: "Modalità scura",
        lightMode: "Modalità chiara",
        refresh: "Aggiorna",
        export: "Esporta",
        download: "Scarica",
        close: "Chiudi",
        details: "Dettagli",
        back: "Indietro",
        name: "Nome",
        address: "Indirizzo",
        phone: "Telefono",
        language: "Lingua",
        role: "Ruolo",
        status: "Stato",
        date: "Data",
        period: "Periodo",
        services: "Servizi",
        guests: "Ospiti",
        property: "Alloggio",
        properties: "Alloggi",
        booking: "Prenotazione",
        bookings: "Prenotazioni",
        user: "Utente",
        users: "Utenti",
        all: "Tutti",
        current: "In corso",
        upcoming: "In arrivo",
        past: "Passato",
        yes: "Sì",
        no: "No",
        required: "Campo obbligatorio",
        optional: "Opzionale",
        people: "persone",
        today: "Oggi",
        month: "Mese",
        week: "Settimana",
        more: "Altro",
        less: 'Meno',
        contact: "Contatto",
        settings: "Impostazioni",
        changePassword: "Cambia password",
        currentPassword: "Password attuale",
        newPassword: "Nuova password",
        confirmPassword: "Conferma password",
        saving: "Salvataggio...",
        languages: {
          fr: "Français",
          en: "English",
          it: "Italiano"
        }
      },
      auth: {
        signIn: "Accedi",
        signUp: "Registrati",
        forgotPassword: "Password dimenticata?",
        resetPassword: "Reimposta password",
        createAccount: "Crea account",
        accountPending: "Account in attesa di attivazione",
        pendingMessage: "Il tuo account deve essere validato da un amministratore prima di poter accedere all'applicazione.",
        accessDenied: "Accesso negato",
        accessDeniedMessage: "Non hai i permessi necessari per accedere a questa pagina.",
        errors: {
          invalidCredentials: "Email o password non validi",
          emailRequired: "Email obbligatoria",
          passwordRequired: "Password obbligatoria",
          passwordLength: "La password deve contenere almeno 6 caratteri",
          emailInvalid: "Email non valida"
        }
      },
      dashboard: {
        title: "Dashboard",
        overview: "Panoramica",
        stats: {
          title: "Statistiche",
          properties: "Alloggi",
          bookings: "Prenotazioni",
          users: "Utenti",
          occupancyRate: "Tasso di occupazione",
          averageNights: "Notti medie",
          todayArrivals: "Arrivi di oggi",
          todayDepartures: "Partenze di oggi",
          currentStays: "Soggiorni in corso"
        },
        activity: {
          title: "Attività recenti",
          noActivity: "Nessuna attività recente",
          types: {
            create: "Creazione",
            update: "Modifica",
            delete: "Eliminazione"
          }
        }
      },
      statistics: {
        title: "Statistiche",
        period: {
          daily: "Giornaliero",
          weekly: "Settimanale",
          monthly: "Mensile",
          yearly: "Annuale"
        },
        metrics: {
          bookings: "Prenotazioni",
          occupancyRate: "Tasso di occupazione",
          averageNights: "Notti medie",
          revenue: "Ricavi",
          nights: "Notti"
        },
        charts: {
          monthlyEvolution: "Evoluzione mensile",
          occupancyByProperty: "Occupazione per alloggio",
          bookingDistribution: "Distribuzione prenotazioni",
          propertyDetails: "Dettagli alloggio"
        }
      },
      bookings: {
        title: "Prenotazioni",
        new: "Nuova prenotazione",
        edit: "Modifica prenotazione",
        delete: "Elimina prenotazione",
        deleteConfirm: "Sei sicuro di voler eliminare questa prenotazione? Questa azione non può essere annullata.",
        filters: {
          services: "Servizi",
          period: "Periodo",
          allServices: "Tutti i servizi",
          cleaning: "Pulizie",
          bedSheets: "Lenzuola",
          both: "Pulizie + Lenzuola",
          currentAndUpcoming: "Soggiorni in corso e futuri",
          allBookings: "Tutte le prenotazioni"
        },
        status: {
          current: "In corso",
          upcoming: "In arrivo",
          past: "Passato"
        },
        form: {
          property: "Alloggio",
          selectProperty: "Seleziona un alloggio",
          guestCount: "Numero di ospiti",
          guestFirstName: "Nome ospite",
          guestLastName: "Cognome ospite",
          checkIn: "Data di arrivo",
          checkOut: "Data di partenza",
          guestEmail: "Email ospite",
          guestPhone: "Telefono ospite",
          doorCode: "Codice di accesso",
          comments: "Commenti",
          cleaningService: "Servizio pulizie",
          bedSheetsService: "Servizio lenzuola",
          validation: {
            propertyRequired: "Alloggio obbligatorio",
            guestCountRequired: "Numero di ospiti obbligatorio",
            guestCountMin: "Minimo 1 ospite",
            guestCountMax: "Il numero di ospiti non può superare la capacità dell'alloggio",
            guestFirstNameRequired: "Nome ospite obbligatorio",
            guestLastNameRequired: "Cognome ospite obbligatorio",
            checkInRequired: "Data di arrivo obbligatoria",
            checkOutRequired: "Data di partenza obbligatoria",
            checkInPast: "La data di arrivo non può essere nel passato",
            checkOutBeforeCheckIn: "La data di partenza deve essere successiva alla data di arrivo",
            emailInvalid: "Email non valida"
          }
        },
        notifications: {
          created: "Prenotazione creata",
          updated: "Prenotazione aggiornata",
          deleted: "Prenotazione eliminata",
          error: "Errore durante il salvataggio"
        }
      },
      properties: {
        title: "Alloggi",
        new: "Nuovo alloggio",
        edit: "Modifica alloggio",
        delete: "Elimina alloggio",
        deleteConfirm: "Sei sicuro di voler eliminare questo alloggio? Questa azione non può essere annullata.",
        search: "Cerca alloggi...",
        tooltip: "Gestisci i tuoi alloggi e le loro informazioni",
        form: {
          name: "Nome alloggio",
          address: "Indirizzo",
          capacity: "Capacità (numero di persone)",
          owner: "Proprietario",
          selectOwner: "Seleziona un proprietario",
          validation: {
            nameRequired: "Nome obbligatorio",
            addressRequired: "Indirizzo obbligatorio",
            capacityRequired: "Capacità obbligatoria",
            capacityMin: "Capacità minima 1 persona",
            capacityMax: "Capacità massima 50 persone",
            ownerRequired: "Proprietario obbligatorio"
          }
        },
        stats: {
          title: "Statistiche alloggio",
          totalBookings: "Totale prenotazioni",
          averageNights: "Notti medie",
          occupancyRate: "Tasso di occupazione",
          monthlyBookings: "Prenotazioni mensili"
        },
        notifications: {
          created: "Alloggio creato",
          updated: "Alloggio aggiornato",
          deleted: "Alloggio eliminato",
          error: "Errore durante il salvataggio"
        }
      },
      users: {
        title: "Utenti",
        new: "Nuovo utente",
        edit: "Modifica utente",
        delete: "Elimina utente",
        deleteConfirm: "Sei sicuro di voler eliminare questo utente? Questa azione non può essere annullata.",
        search: "Cerca utenti...",
        roles: {
          guest: "Ospite",
          support: "Supporto",
          owner: "Proprietario",
          admin: "Amministratore",
          super_admin: "Super Amministratore"
        },
        form: {
          email: "Email",
          password: "Password",
          firstName: "Nome",
          lastName: "Cognome",
          phone: "Telefono",
          role: "Ruolo",
          language: "Lingua preferita",
          validation: {
            emailRequired: "Email obbligatoria",
            emailInvalid: "Email non valida",
            passwordRequired: "Password obbligatoria",
            passwordLength: "La password deve contenere almeno 6 caratteri",
            firstNameRequired: "Nome obbligatorio",
            lastNameRequired: "Cognome obbligatorio"
          }
        },
        notifications: {
          created: "Utente creato",
          updated: "Utente aggiornato",
          deleted: "Utente eliminato",
          error: "Errore durante il salvataggio"
        }
      }
    }
  }
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: localStorage.getItem('language') || 'fr',
    fallbackLng: 'fr',
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;